import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngxs/store';
import { Observable, Subscription, combineLatest, filter, take } from 'rxjs';
import { IAlertResponse } from 'src/app/alert/models/alert.model';
import { AlertState } from 'src/app/alert/state/alert.state';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  IDepartmentInfo,
  IUserAccess,
} from 'src/app/shared/user-view/models/tenant-user-access.model';
import { DocumentState } from 'src/app/document-user/state/document.state';
import { UiState } from '../../state/ui.state';
import { Privileges } from '../../privileges/privileges';
import { LocalStorageService } from '../../services/local-storage.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
  alerts$: Observable<IAlertResponse[]> = this.store.select(AlertState.getAlerts);
  currentTenantId$: Observable<string> = this.store.select(TenantUserAccessState.currentTenantId);
  currentDepartmentId$: Observable<string> = this.store.select(
    TenantUserAccessState.currentDepartmentId
  );
  isProductOwner$: Observable<boolean | undefined> = this.store.select(
    TenantUserAccessState.isProductOwner
  );
  isDarkMode$: Observable<boolean> = this.store.select(UiState.isDarkMode);
  userAccess$: Observable<IUserAccess> = this.store.select(TenantUserAccessState.userAccess);
  availableDepartments$: Observable<IDepartmentInfo[]> = this.store.select(
    TenantUserAccessState.getAllAvailableDepartments
  );

  // Due List numbers
  expiredWarnings = 0;
  soonExpiringWarnings = 0;
  totalWarnings = 0;
  warningsLoaded = false;
  expiredCompetences = 0;
  soonExpiringCompetences = 0;
  competencesLoaded = false;
  userNameAndSurname: string;
  darkMode: boolean;

  // Alerts
  alertSum = 0;

  // Non-Conformances
  ncOpen: number;
  ncCompleted: number;
  ncStillOpenPercent: number;
  canReadNC = false;

  // Orders
  ordersSentToSupplier = 0;
  canReadOrders = false;

  // Maintenance Job
  canReadMaintenanceJob = false;
  canReadDocuments = false;
  readReceiptsEnabled = select(DocumentState.getCurrentDepartmentReadReceiptSettingsEnabled);

  isUserProductOwner = false;
  displayDashboard = false;
  showWelcomeMessage = false;
  Privileges = Privileges;

  private tenantId: string;
  private departmentId: string;
  private subscription = new Subscription();

  constructor(
    private store: Store,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.isDarkMode();
    this.checkIfUserIsProductOwner();
    this.listenToTenantOrDepartmentChanges();
    this.getAlerts();
    this.getUserNameAndSurname();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private listenToTenantOrDepartmentChanges(): void {
    this.subscription.add(
      combineLatest([
        this.currentTenantId$,
        this.currentDepartmentId$,
        this.availableDepartments$,
      ]).subscribe((result: [string, string, IDepartmentInfo[]]) => {
        const tenantId = result[0];
        const depId = result[1];
        const availableDepartments = result[2];
        const selectionChanged = this.tenantId !== tenantId || this.departmentId !== depId;

        if (tenantId !== undefined) {
          this.displayDashboard = true;
        }

        if (this.tenantId === undefined) {
          this.tenantId = tenantId;
        }
        if (this.departmentId === undefined) {
          this.departmentId = depId;
        }
        if (availableDepartments.length === 0) {
          this.displayDashboard = false;
        }

        if (selectionChanged) {
          this.tenantId = tenantId;
          this.departmentId = depId;
        }

        if (depId !== ALL_DEPS_TOKEN && selectionChanged) {
          if (this.isUserProductOwner) {
            this.getNonConformances();
            this.getOrders();
          } else {
            if (this.canReadOrders) {
              this.getOrders();
            }
            if (this.canReadNC) {
              this.getNonConformances();
            }
          }
        }
      })
    );
  }

  private checkIfUserIsProductOwner(): void {
    this.subscription.add(
      this.isProductOwner$.subscribe((productOwner: boolean | undefined) => {
        if (productOwner) {
          this.getOrders();
          this.getNonConformances();
          this.isUserProductOwner = true;
          this.canReadMaintenanceJob = true;
          this.canReadNC = true;
        } else {
          this.checkModulePrivileges();
          this.isUserProductOwner = false;
        }
      })
    );
  }

  private checkModulePrivileges(): void {
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.getPrivilegesFlat)
        .subscribe((privileges: string[]) => {
          if (privileges.includes(Privileges.OrderRead)) {
            this.canReadOrders = true;
            this.getOrders();
          } else {
            this.canReadOrders = false;
            this.ordersSentToSupplier = 0;
          }
          if (privileges.includes(Privileges.NonConformanceRead)) {
            this.canReadNC = true;
            this.getNonConformances();
          } else {
            this.canReadNC = false;
            this.ncOpen = 0;
            this.ncCompleted = 0;
            this.ncStillOpenPercent = 0;
          }
          this.canReadDocuments = privileges.includes(Privileges.DocumentRead);
          this.canReadMaintenanceJob = privileges.includes(Privileges.MaintenanceJobRead);
        })
    );
  }

  private getUserNameAndSurname(): void {
    this.subscription.add(
      this.userAccess$
        .pipe(
          filter((userAccess) => !!userAccess?.id),
          take(1)
        )
        .subscribe((userAccess: IUserAccess) => {
          this.userNameAndSurname = userAccess.name;
          const storageKey = `dashboard-welcome-seen:${userAccess.id}`;
          const hasSeenWelcome = this.localStorageService.getSavedState(storageKey) === true;
          this.showWelcomeMessage = !hasSeenWelcome;

          if (!hasSeenWelcome) {
            this.localStorageService.setSavedState(true, storageKey);
          }
        })
    );
  }

  private isDarkMode(): void {
    this.subscription.add(
      this.isDarkMode$.subscribe((darkMode: boolean) => {
        this.darkMode = darkMode;
      })
    );
  }

  private getAlerts(): void {
    this.subscription.add(
      this.alerts$.subscribe((alerts: IAlertResponse[]) => {
        this.alertSum = alerts.length ?? 0;
      })
    );
  }

  // TODO Illia: commented, maybe will be removed later https://dev.azure.com/ccomas/CCOM%20X/_workitems/edit/11172

  getDueListCounts(): void {
    // this.subscription.add(
    //   this.store
    //     .select(DueListState.getDueListCounts)
    //     .pipe(
    //       tap((counts: IDueListCounts | undefined) => {
    //         if (counts) {
    //           this.expiredWarnings = counts.expiredWarnings;
    //           this.soonExpiringWarnings = counts.expiringWarnings;
    //           this.totalWarnings = counts.expiredWarnings + counts.expiringWarnings;
    //           this.expiredCompetences = counts.expiredCompetences;
    //           this.soonExpiringCompetences = counts.expiringCompetences;
    //           if (this.expiredWarnings > 0) {
    //             setTimeout(() => {
    //               this.warningsLoaded = true;
    //             });
    //           } else {
    //             this.warningsLoaded = false;
    //           }
    //           if (this.expiredCompetences > 0) {
    //             setTimeout(() => {
    //               this.competencesLoaded = true;
    //             });
    //           } else {
    //             this.competencesLoaded = false;
    //           }
    //         }
    //       })
    //     )
    //     .subscribe()
    // );
  }

  private getNonConformances(): void {
    // const tempFilterObj = new MainTableFilterModel(MainTableFilterModulesEnum.NonConformances);
    // tempFilterObj.pageIndex = 0;
    // // eslint-disable-next-line no-magic-numbers
    // tempFilterObj.pageSize = 10000;
    // const ncStateOpen = NonConformanceStatus.Open;
    // const ncStateCompleted = NonConformanceStatus.Completed;
    // this.subscription.add(
    //   forkJoin([
    //     this.nonConformanceApiService.getAllNonConformancesWithStates(
    //       [ncStateOpen, ncStateCompleted],
    //       tempFilterObj,
    //       false
    //     ),
    //   ]).subscribe(([response]) => {
    //     this.ncOpen =
    //       response.data?.filter((nc) => nc.nonConformanceState === ncStateOpen).length ?? 0;
    //     this.ncCompleted =
    //       response.data?.filter((nc) => nc.nonConformanceState === ncStateCompleted).length ?? 0;
    //     const total = this.ncOpen + this.ncCompleted;
    //     this.ncStillOpenPercent = total > 0 ? (this.ncOpen / total) * 100 : 0;
    //   })
    // );
  }

  private getOrders(): void {
    // const tempFilterObj = new MainTableFilterModel(MainTableFilterModulesEnum.Orders);
    // tempFilterObj.pageIndex = 0;
    // // eslint-disable-next-line no-magic-numbers
    // tempFilterObj.pageSize = 10000;
    // const orderStageSentToSupplier = OrderStatus.SentToSupplier;
    // this.subscription.add(
    //   this.orderApiService
    //     .getOrders(orderStageSentToSupplier, tempFilterObj)
    //     .subscribe((apiResponse: ApiResponse<IOrderResponse[]>) => {
    //       this.ordersSentToSupplier = apiResponse.data?.length ?? 0;
    //     })
    // );
  }
}
